import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar,
} from 'recharts';
import { STATUS_LABELS, PARTNERSHIP_TYPE_LABELS, fmtCurrency } from '../../lib/utils';
import { toast } from 'sonner';

const NAVY = '#0E1944';
const MAGENTA = '#E91E8C';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const METRIC_LABELS = {
  new_contacts: 'New Contacts', active_contacts: 'Active Contacts', inactive_contacts: 'Inactive Contacts',
  archived_contacts: 'Archived Contacts', conversions: 'Conversions', revenue: 'Closed Revenue',
  emails_sent: 'Emails Sent', emails_delivered: 'Emails Delivered', emails_bounced: 'Emails Bounced',
  emails_opened: 'Emails Opened', emails_clicked: 'Emails Clicked',
  appointments_booked: 'Appointments Booked', appointments_completed: 'Appointments Completed',
  appointments_canceled: 'Appointments Canceled', appointments_no_show: 'No-Shows',
  followups_completed: 'Follow-Ups Completed', activities: 'Total Activities',
};
const COMPARE_CARDS = ['new_contacts', 'conversions', 'revenue', 'emails_sent', 'emails_opened', 'appointments_booked', 'followups_completed', 'activities'];
const ACTIVITY_TYPES = ['email', 'call', 'linkedin', 'meeting', 'note', 'followup', 'email_campaign'];

const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

function DeltaBadge({ d }) {
  if (!d) return null;
  const up = d.delta > 0;
  const flat = d.delta === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${flat ? 'text-slate-400' : up ? 'text-emerald-600' : 'text-rose-500'}`}>
      <Icon className="w-3 h-3" /> {d.delta > 0 ? '+' : ''}{Math.round(d.delta * 100) / 100} ({d.pct > 0 ? '+' : ''}{d.pct}%)
    </span>
  );
}

export function HistoricalAnalytics({ users = [], campaigns = [] }) {
  const now = new Date();
  const curMonth = now.toISOString().slice(0, 7);
  const [preset, setPreset] = useState('12m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filters, setFilters] = useState({ assigned_to: '', contact_type: '', status: '', lead_source: '', activity_type: '', campaign_id: '' });
  const [data, setData] = useState(null);
  const [contactTypes, setContactTypes] = useState([]);
  const [chartMetric, setChartMetric] = useState('new_contacts');

  const range = useMemo(() => {
    if (preset === 'custom') return { date_from: from || undefined, date_to: to || undefined };
    if (preset === 'ytd') return { date_from: `${now.getFullYear()}-01`, date_to: curMonth };
    const n = preset === '6m' ? 5 : preset === '24m' ? 23 : 11;
    const d = new Date(now.getFullYear(), now.getMonth() - n, 1);
    return { date_from: d.toISOString().slice(0, 7), date_to: curMonth };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, from, to]);

  useEffect(() => {
    api.get('/comms/crm-filters').then((r) => setContactTypes(r.data.contact_types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { ...range };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get('/analytics/history', { params }).then((r) => setData(r.data)).catch(() => {});
  }, [range, filters]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ ...range, ...filters }).forEach(([k, v]) => { if (v) params.set(k, v); });
    const token = localStorage.getItem('aawta_token');
    try {
      const resp = await fetch(`${API_BASE}/api/analytics/history/export.csv?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `aawta-analytics-history.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export started');
    } catch { toast.error('Export failed'); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === '_all' ? '' : v }));
  const chartData = (data?.months || []).map((m) => ({ ...m, label: monthLabel(m.month) }));

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-5" data-testid="historical-analytics">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-magenta">Historical Reporting</div>
          <h3 className="font-heading text-xl font-bold text-navy">Month-to-Month Performance</h3>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="history-export-csv">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Range + filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
          {[['6m', 'Last 6 mo'], ['12m', 'Last 12 mo'], ['24m', 'Last 24 mo'], ['ytd', 'Year to Date'], ['custom', 'Custom']].map(([v, l]) => (
            <button key={v} onClick={() => setPreset(v)} className={`px-3 py-2 ${preset === v ? 'bg-navy text-white' : 'bg-white text-slate-600'}`} data-testid={`history-range-${v}`}>{l}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <div><Label className="text-[10px]">From</Label><Input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-36" data-testid="history-from" /></div>
            <div><Label className="text-[10px]">To</Label><Input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-36" data-testid="history-to" /></div>
          </>
        )}
        <Select value={filters.assigned_to || '_all'} onValueChange={(v) => setF('assigned_to', v)}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="history-filter-member"><SelectValue placeholder="Team member" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Team Members</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.contact_type || '_all'} onValueChange={(v) => setF('contact_type', v)}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="history-filter-contact-type"><SelectValue placeholder="Contact type" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Contact Types</SelectItem>{contactTypes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || '_all'} onValueChange={(v) => setF('status', v)}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="history-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Statuses</SelectItem>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.activity_type || '_all'} onValueChange={(v) => setF('activity_type', v)}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="history-filter-activity"><SelectValue placeholder="Activity type" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Activity Types</SelectItem>{ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.campaign_id || '_all'} onValueChange={(v) => setF('campaign_id', v)}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="history-filter-campaign"><SelectValue placeholder="Campaign" /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All Campaigns</SelectItem>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!data ? <div className="text-sm text-slate-400 py-8 text-center">Loading history…</div> : (
        <>
          {/* Current vs last month */}
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              {data.current_month && monthLabel(data.current_month)} vs {data.previous_month && monthLabel(data.previous_month)}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="history-compare-cards">
              {COMPARE_CARDS.map((k) => {
                const d = data.comparison?.[k];
                return (
                  <div key={k} className="bg-slate-50 rounded-xl p-3.5" data-testid={`compare-${k}`}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{METRIC_LABELS[k]}</div>
                    <div className="font-heading text-xl font-bold text-navy mt-0.5">
                      {k === 'revenue' ? fmtCurrency(d?.current || 0) : d?.current ?? 0}
                    </div>
                    <DeltaBadge d={d} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trend chart */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Monthly Trend</div>
              <Select value={chartMetric} onValueChange={setChartMetric}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="history-chart-metric"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(METRIC_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => (chartMetric === 'revenue' ? `$${v / 1000}k` : v)} />
                <Tooltip formatter={(v) => (chartMetric === 'revenue' ? fmtCurrency(v) : v)} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
                <Line type="monotone" dataKey={chartMetric} name={METRIC_LABELS[chartMetric]} stroke={MAGENTA} strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Email + appointments combo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Email Performance by Month</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} />
                  <YAxis stroke="#94A3B8" fontSize={10} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="emails_sent" name="Sent" fill={NAVY} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="emails_opened" name="Opened" fill={MAGENTA} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="emails_bounced" name="Bounced" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Appointments by Month</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} />
                  <YAxis stroke="#94A3B8" fontSize={10} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="appointments_booked" name="Booked" fill={NAVY} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="appointments_completed" name="Completed" fill="#10B981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="appointments_canceled" name="Canceled" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Monthly Breakdown (YTD totals in footer)</div>
            <table className="w-full text-xs" data-testid="history-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-3 font-semibold text-slate-500">Month</th>
                  {Object.entries(METRIC_LABELS).map(([k, l]) => (
                    <th key={k} className="text-right py-2 px-2 font-semibold text-slate-500 whitespace-nowrap">{l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...(data.months || [])].reverse().map((m) => (
                  <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`history-row-${m.month}`}>
                    <td className="py-1.5 pr-3 font-semibold text-navy whitespace-nowrap">{monthLabel(m.month)}</td>
                    {Object.keys(METRIC_LABELS).map((k) => (
                      <td key={k} className="text-right py-1.5 px-2 text-slate-600">{k === 'revenue' ? fmtCurrency(m[k]) : m[k]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-bold text-navy">
                  <td className="py-2 pr-3">YTD</td>
                  {Object.keys(METRIC_LABELS).map((k) => (
                    <td key={k} className="text-right py-2 px-2">
                      {data.ytd?.[k] === undefined ? '—' : k === 'revenue' ? fmtCurrency(data.ytd[k]) : data.ytd[k]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
